import { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import type { UserPermissions } from '../types/auth'
import type { AgentUI } from '../types/agents'

import { Icon } from '@/design-system/atoms/Icon'
import { useSidebar } from '../context/sidebar'
import { useAuth } from '../context/auth'
import { useAgents } from '../context/agents'

type NavItem = {
  name: string
  icon: React.ReactNode
  path?: string
  subItems?: { name: string; path: string; pro?: boolean; new?: boolean }[]
}

// Função para gerar menu de agentes baseado nos agentes disponíveis da API
const getAgentMenuItems = (
  agents: AgentUI[],
  permissions: UserPermissions | null
): NavItem[] => {
  // Filtrar agentes baseado nas permissões
  const allowedAgents = agents.filter(
    agent =>
      agent.status === 'active' &&
      (permissions?.allowedAgents?.includes(agent.type) ||
        permissions?.canManageUsers)
  )

  // Retornar agentes diretamente como itens do menu
  return allowedAgents.map(agent => ({
    name: agent.name,
    icon: <Icon name="bolt" />,
    path: `/agents/${agent.type}/chat`,
  }))
}

const AppSidebar: React.FC = () => {
  const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar()
  const { permissions } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  // Use agents from context - eliminates duplicate API call!
  const { agents: availableAgents, isLoading: agentsLoading } = useAgents()

  const baseNavItems: NavItem[] = useMemo(() => {
    const items: NavItem[] = [
      {
        name: 'Conhecimento',
        icon: <Icon name="docs" />,
        path: '/kb',
      },
    ]
    return items
  }, [])

  // Gerar menu de agentes dinamicamente baseado nos agentes carregados da API
  const agentNavItems = useMemo(
    () =>
      agentsLoading ? [] : getAgentMenuItems(availableAgents, permissions),
    [availableAgents, permissions, agentsLoading]
  )

  const [openSubmenu, setOpenSubmenu] = useState<{
    type: 'agents'
    index: number
  } | null>(null)
  const [subMenuHeight, setSubMenuHeight] = useState<Record<string, number>>({})
  const subMenuRefs = useRef<Record<string, HTMLDivElement | null>>({})

  // const isActive = (path: string) => location.pathname === path;
  const isActive = useCallback(
    (path: string) => location.pathname === path,
    [location.pathname]
  )

  // Função memoizada para encontrar submenu ativo
  const findActiveSubmenu = useCallback(() => {
    const menuTypes: Array<{
      type: 'agents'
      items: NavItem[]
    }> = [{ type: 'agents', items: agentNavItems }]

    for (const { type, items } of menuTypes) {
      for (let index = 0; index < items.length; index++) {
        const nav = items[index]
        if (nav.subItems) {
          for (const subItem of nav.subItems) {
            if (isActive(subItem.path)) {
              return { type, index }
            }
          }
        }
      }
    }
    return null
  }, [agentNavItems, isActive])

  useEffect(() => {
    const activeSubmenu = findActiveSubmenu()
    setOpenSubmenu(activeSubmenu)
  }, [findActiveSubmenu])

  useEffect(() => {
    if (openSubmenu !== null) {
      const key = `${openSubmenu.type}-${openSubmenu.index}`
      if (subMenuRefs.current[key]) {
        setSubMenuHeight(prevHeights => ({
          ...prevHeights,
          [key]: subMenuRefs.current[key]?.scrollHeight || 0,
        }))
      }
    }
  }, [openSubmenu])

  const handleSubmenuToggle = (index: number, menuType: 'agents') => {
    setOpenSubmenu(prevOpenSubmenu => {
      if (
        prevOpenSubmenu &&
        prevOpenSubmenu.type === menuType &&
        prevOpenSubmenu.index === index
      ) {
        return null
      }
      return { type: menuType, index }
    })
  }

  const handleNavigation = (path: string, e?: React.MouseEvent) => {
    e?.preventDefault()
    navigate(path)
  }

  const renderMenuItems = (items: NavItem[], menuType: 'agents') => (
    <ul className="flex flex-col gap-4">
      {items.map((nav, index) => (
        <li key={nav.name}>
          {nav.subItems ? (
            <button
              onClick={() => handleSubmenuToggle(index, menuType)}
              className={`menu-item group ${
                openSubmenu?.type === menuType && openSubmenu?.index === index
                  ? 'menu-item-active'
                  : 'menu-item-inactive'
              } cursor-pointer ${
                !isExpanded && !isHovered
                  ? 'lg:justify-center'
                  : 'lg:justify-start'
              }`}
            >
              <span
                className={`menu-item-icon-size  ${
                  openSubmenu?.type === menuType && openSubmenu?.index === index
                    ? 'menu-item-icon-active'
                    : 'menu-item-icon-inactive'
                }`}
              >
                {nav.icon}
              </span>
              {(isExpanded || isHovered || isMobileOpen) && (
                <span className="menu-item-text">{nav.name}</span>
              )}
              {(isExpanded || isHovered || isMobileOpen) && (
                <Icon
                  name="chevron-down"
                  className={`ml-auto transition-transform duration-200 ${
                    openSubmenu?.type === menuType &&
                    openSubmenu?.index === index
                      ? 'rotate-180 text-brand-500'
                      : ''
                  }`}
                />
              )}
            </button>
          ) : (
            nav.path && (
              <button
                onClick={e => handleNavigation(nav.path!, e)}
                className={`menu-item group ${
                  isActive(nav.path) ? 'menu-item-active' : 'menu-item-inactive'
                }`}
              >
                <span
                  className={`menu-item-icon-size ${
                    isActive(nav.path)
                      ? 'menu-item-icon-active'
                      : 'menu-item-icon-inactive'
                  }`}
                >
                  {nav.icon}
                </span>
                {(isExpanded || isHovered || isMobileOpen) && (
                  <span className="menu-item-text">{nav.name}</span>
                )}
              </button>
            )
          )}
          {nav.subItems && (isExpanded || isHovered || isMobileOpen) && (
            <div
              ref={el => {
                subMenuRefs.current[`${menuType}-${index}`] = el
              }}
              className="overflow-hidden transition-all duration-300"
              style={{
                height:
                  openSubmenu?.type === menuType && openSubmenu?.index === index
                    ? `${subMenuHeight[`${menuType}-${index}`]}px`
                    : '0px',
              }}
            >
              <ul className="mt-2 space-y-1 ml-9">
                {nav.subItems.map(subItem => (
                  <li key={subItem.name}>
                    <button
                      onClick={e => handleNavigation(subItem.path, e)}
                      className={`menu-dropdown-item ${
                        isActive(subItem.path)
                          ? 'menu-dropdown-item-active'
                          : 'menu-dropdown-item-inactive'
                      }`}
                    >
                      {subItem.name}
                      <span className="flex items-center gap-1 ml-auto">
                        {subItem.new && (
                          <span
                            className={`ml-auto ${
                              isActive(subItem.path)
                                ? 'menu-dropdown-badge-active'
                                : 'menu-dropdown-badge-inactive'
                            } menu-dropdown-badge`}
                          >
                            new
                          </span>
                        )}
                        {subItem.pro && (
                          <span
                            className={`ml-auto ${
                              isActive(subItem.path)
                                ? 'menu-dropdown-badge-active'
                                : 'menu-dropdown-badge-inactive'
                            } menu-dropdown-badge`}
                          >
                            pro
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </li>
      ))}
    </ul>
  )

  return (
    <aside
      className={`fixed mt-0 flex flex-col lg:mt-0 top-0 px-5 left-0 bg-white dark:bg-gray-900 dark:border-gray-800 text-gray-900 h-screen transition-all duration-300 ease-in-out z-50 border-r border-gray-200 
        ${
          isExpanded || isMobileOpen
            ? 'w-[290px]'
            : isHovered
              ? 'w-[290px]'
              : 'w-[90px]'
        }
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`py-3 lg:py-0 lg:pt-0 lg:pb-0 lg:h-[76px] flex items-center gap-3 ${
          !isExpanded && !isHovered ? 'justify-center' : 'justify-start'
        }`}
      >
        <Link to="/" className="flex items-center gap-3">
          <img
            src="/images/logo/logo-icon.png"
            alt="MDi Optimus"
            className="h-12 w-12 object-contain dark:brightness-110 flex-shrink-0"
          />
          {(isExpanded || isHovered || isMobileOpen) && (
            <span className="text-lg font-semibold text-gray-900 dark:text-white whitespace-nowrap">
              MDi Optimus
            </span>
          )}
        </Link>
      </div>

      {/* Separador sutil */}
      <div className="border-b border-gray-200 dark:border-gray-800 mb-4"></div>

      <div className="flex flex-col overflow-y-auto duration-300 ease-linear no-scrollbar">
        <nav className="mb-6">
          <div className="flex flex-col gap-6">
            {/* Agentes primeiro (home) */}
            {agentNavItems.length > 0 && (
              <div>{renderMenuItems(agentNavItems, 'agents')}</div>
            )}

            {/* Kanban e outros */}
            <div>{renderMenuItems(baseNavItems, 'agents')}</div>
          </div>
        </nav>
      </div>
    </aside>
  )
}

export default AppSidebar
